<script setup>
import { computed, onMounted, ref, reactive, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import agentAPI from 'dashboard/api/aifocus/agent';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

useMapGetter('aifocusAgents/getUIFlags');

// Chatwoot Tools Templates
const chatwootToolsTemplates = ref([]);
const chatwootToolsCategories = ref({});
const isLoadingChatwootTools = ref(false);

// Built-in tools available in Agno
const builtinTools = ref([
  {
    id: 'web_search',
    name: 'Web Search',
    description: 'Search the web for information',
    icon: 'i-lucide-search',
    category: 'search',
  },
  {
    id: 'calculator',
    name: 'Calculator',
    description: 'Perform mathematical calculations',
    icon: 'i-lucide-calculator',
    category: 'math',
  },
  {
    id: 'code_interpreter',
    name: 'Code Interpreter',
    description: 'Execute Python code',
    icon: 'i-lucide-code',
    category: 'code',
  },
  {
    id: 'file_reader',
    name: 'File Reader',
    description: 'Read and analyze files',
    icon: 'i-lucide-file-text',
    category: 'files',
  },
  {
    id: 'image_analyzer',
    name: 'Image Analyzer',
    description: 'Analyze and describe images',
    icon: 'i-lucide-image',
    category: 'vision',
  },
  {
    id: 'json_parser',
    name: 'JSON Parser',
    description: 'Parse and manipulate JSON data',
    icon: 'i-lucide-braces',
    category: 'data',
  },
  {
    id: 'audio_transcriber',
    name: 'Audio Transcriber',
    description: 'Transcribe and analyze audio files',
    icon: 'i-lucide-mic',
    category: 'audio',
  },
  {
    id: 'conversation_summary',
    name: 'Resumo de Conversa',
    description:
      'Resumir conversa e enviar mensagem privada ao transferir para atendente',
    icon: 'i-lucide-file-text',
    category: 'summary',
  },
  {
    id: 'chatwoot_images',
    name: 'Envio de Imagens',
    description:
      'Enviar imagens de produtos e fotos como anexo para o cliente na conversa',
    icon: 'i-lucide-image',
    category: 'images',
  },
  {
    id: 'chatwoot_transfer',
    name: 'Transferencia para Humano',
    description:
      'Transferir conversa para atendente humano com distribuicao em cascata na equipe',
    icon: 'i-lucide-arrow-right-left',
    category: 'transfer',
  },
]);

// Destructive tools that require explicit opt-in (mirror of backend tool_permissions.py)
const destructiveTools = [
  { id: 'create_label', label: 'Criar etiqueta', group: 'Etiquetas' },
  {
    id: 'add_label_to_conversation',
    label: 'Adicionar etiqueta a conversa',
    group: 'Etiquetas',
  },
  {
    id: 'remove_label_from_conversation',
    label: 'Remover etiqueta da conversa',
    group: 'Etiquetas',
  },
  {
    id: 'add_conversation_note',
    label: 'Adicionar nota a conversa',
    group: 'Notas',
  },
  {
    id: 'add_contact_note',
    label: 'Adicionar nota ao contato',
    group: 'Notas',
  },
  { id: 'add_to_funnel', label: 'Adicionar ao funil', group: 'Funil/Deals' },
  {
    id: 'move_conversation_stage',
    label: 'Mover estagio da conversa',
    group: 'Funil/Deals',
  },
  { id: 'update_deal', label: 'Atualizar deal', group: 'Funil/Deals' },
  {
    id: 'mark_deal_won',
    label: 'Marcar deal como ganho',
    group: 'Funil/Deals',
  },
  {
    id: 'mark_deal_lost',
    label: 'Marcar deal como perdido',
    group: 'Funil/Deals',
  },
  {
    id: 'transfer_to_human',
    label: 'Transferir para humano',
    group: 'Conversa',
  },
  { id: 'assign_conversation', label: 'Atribuir conversa', group: 'Conversa' },
  {
    id: 'change_conversation_status',
    label: 'Alterar status da conversa',
    group: 'Conversa',
  },
  {
    id: 'send_image_to_conversation',
    label: 'Enviar imagem',
    group: 'Mensagens',
  },
  {
    id: 'send_private_message',
    label: 'Enviar mensagem privada',
    group: 'Mensagens',
  },
  { id: 'update_contact', label: 'Atualizar contato', group: 'Contato' },
  { id: 'add_product', label: 'Adicionar produto', group: 'Produtos' },
  { id: 'remove_product', label: 'Remover produto', group: 'Produtos' },
  {
    id: 'create_appointment',
    label: 'Criar agendamento',
    group: 'Agendamentos',
  },
  {
    id: 'update_appointment',
    label: 'Atualizar agendamento',
    group: 'Agendamentos',
  },
  {
    id: 'cancel_appointment',
    label: 'Cancelar agendamento',
    group: 'Agendamentos',
  },
];

// State
const agentTools = ref([]);
const customTools = ref({ http_tools: [] });
const toolPermissions = ref({});
const isSaving = ref(false);

const destructiveToolGroups = computed(() => {
  return destructiveTools.reduce((groups, tool) => {
    if (!groups[tool.group]) groups[tool.group] = [];
    groups[tool.group].push(tool);
    return groups;
  }, {});
});

const getToolPerm = id =>
  toolPermissions.value[id] || { enabled: false, note: '' };

const saveToolPermissions = async updated => {
  if (!props.agentId) return;
  try {
    isSaving.value = true;
    const nextConfig = {
      ...(agent.value?.config || {}),
      tool_permissions: updated,
    };
    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      config: nextConfig,
    });
    toolPermissions.value = updated;
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar permissoes');
  } finally {
    isSaving.value = false;
  }
};

const toggleToolPerm = id => {
  const current = getToolPerm(id);
  const updated = {
    ...toolPermissions.value,
    [id]: { ...current, enabled: !current.enabled },
  };
  saveToolPermissions(updated);
};

let noteTimer = null;
const updateToolNote = (id, note) => {
  const current = getToolPerm(id);
  toolPermissions.value = {
    ...toolPermissions.value,
    [id]: { ...current, note },
  };
  if (noteTimer) clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    saveToolPermissions(toolPermissions.value);
  }, 800);
};

// Dialog ref
const httpToolDialogRef = ref(null);
const editingToolIndex = ref(null);
const activeTab = ref('basic');

// HTTP Tool Form
const httpToolForm = reactive({
  name: '',
  description: '',
  method: 'GET',
  endpoint: '',
  headers: {},
  values: {},
  parameters: {
    path_params: {},
    query_params: {},
    body_params: {},
  },
  error_handling: {
    timeout: 30,
    retry_count: 0,
    fallback_response: {},
  },
});

// Temp arrays for dynamic editing
const headersList = ref([]);
const valuesList = ref([]);
const pathParamsList = ref([]);
const queryParamsList = ref([]);
const bodyParamsList = ref([]);

// Options for ComboBox
const httpMethodOptions = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const paramTypeOptions = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

const updateAgentFromData = agentData => {
  if (!agentData) return;
  agentTools.value = agentData.agent_tools || [];
  customTools.value = agentData.custom_tools || { http_tools: [] };
  toolPermissions.value =
    (agentData.config && agentData.config.tool_permissions) || {};
};

const fetchChatwootTools = async () => {
  try {
    isLoadingChatwootTools.value = true;
    const response = await agentAPI.getChatwootTools();
    chatwootToolsTemplates.value = response.data.templates || [];
    chatwootToolsCategories.value = response.data.categories || {};
  } catch {
    // Silently fail - templates will be empty
  } finally {
    isLoadingChatwootTools.value = false;
  }
};

const isChatwootToolEnabled = templateId => {
  return agentTools.value.includes(templateId);
};

const toggleChatwootTool = async templateId => {
  if (!props.agentId) {
    useAlert('Erro: ID do agente nao encontrado');
    return;
  }

  const newTools = isChatwootToolEnabled(templateId)
    ? agentTools.value.filter(t => t !== templateId)
    : [...agentTools.value, templateId];

  try {
    isSaving.value = true;
    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      agent_tools: newTools,
    });
    agentTools.value = newTools;
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar ferramentas');
  } finally {
    isSaving.value = false;
  }
};

const getChatwootToolIcon = category => {
  const icons = {
    kanban: 'i-lucide-kanban',
    products: 'i-lucide-shopping-cart',
    appointments: 'i-lucide-calendar',
    notes: 'i-lucide-file-text',
    labels: 'i-lucide-tag',
    contacts: 'i-lucide-user',
    conversations: 'i-lucide-message-square',
    summary: 'i-lucide-file-text',
    images: 'i-lucide-image',
    transfer: 'i-lucide-arrow-right-left',
  };
  return icons[category] || 'i-lucide-tool';
};

onMounted(() => {
  if (agent.value) {
    updateAgentFromData(agent.value);
  }
  fetchChatwootTools();
});

watch(
  agent,
  newAgent => {
    if (newAgent && !isSaving.value) {
      updateAgentFromData(newAgent);
    }
  },
  { deep: false }
);

const isToolEnabled = toolId => {
  return agentTools.value.includes(toolId);
};

const toggleTool = async toolId => {
  if (!props.agentId) {
    useAlert('Erro: ID do agente nao encontrado');
    return;
  }

  const newTools = isToolEnabled(toolId)
    ? agentTools.value.filter(t => t !== toolId)
    : [...agentTools.value, toolId];

  try {
    isSaving.value = true;
    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      agent_tools: newTools,
    });
    agentTools.value = newTools;
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar ferramentas');
  } finally {
    isSaving.value = false;
  }
};

const resetHttpToolForm = () => {
  httpToolForm.name = '';
  httpToolForm.description = '';
  httpToolForm.method = 'GET';
  httpToolForm.endpoint = '';
  httpToolForm.headers = {};
  httpToolForm.values = {};
  httpToolForm.parameters = {
    path_params: {},
    query_params: {},
    body_params: {},
  };
  httpToolForm.error_handling = {
    timeout: 30,
    retry_count: 0,
    fallback_response: {},
  };
  headersList.value = [];
  valuesList.value = [];
  pathParamsList.value = [];
  queryParamsList.value = [];
  bodyParamsList.value = [];
};

const openHttpToolModal = (tool = null, index = null) => {
  activeTab.value = 'basic';

  if (tool && index !== null) {
    editingToolIndex.value = index;
    httpToolForm.name = tool.name || '';
    httpToolForm.description = tool.description || '';
    httpToolForm.method = tool.method || 'GET';
    httpToolForm.endpoint = tool.endpoint || '';
    httpToolForm.headers = tool.headers || {};
    httpToolForm.values = tool.values || {};
    httpToolForm.parameters = tool.parameters || {
      path_params: {},
      query_params: {},
      body_params: {},
    };
    httpToolForm.error_handling = tool.error_handling || {
      timeout: 30,
      retry_count: 0,
      fallback_response: {},
    };

    headersList.value = Object.entries(httpToolForm.headers).map(
      ([key, value]) => ({ key, value })
    );
    valuesList.value = Object.entries(httpToolForm.values).map(
      ([key, value]) => ({ key, value })
    );
    pathParamsList.value = Object.entries(
      httpToolForm.parameters.path_params || {}
    ).map(([key, value]) => ({ key, value }));
    queryParamsList.value = Object.entries(
      httpToolForm.parameters.query_params || {}
    ).map(([key, value]) => ({ key, value }));
    bodyParamsList.value = Object.entries(
      httpToolForm.parameters.body_params || {}
    ).map(([key, value]) => ({
      key,
      type: value?.type || 'string',
      required: value?.required || false,
      description: value?.description || '',
    }));
  } else {
    editingToolIndex.value = null;
    resetHttpToolForm();
  }
  httpToolDialogRef.value.open();
};

const saveHttpTool = async () => {
  if (!props.agentId) {
    useAlert('Erro: ID do agente nao encontrado');
    return;
  }

  if (!httpToolForm.name || !httpToolForm.endpoint) {
    useAlert('Nome e Endpoint sao obrigatorios');
    return;
  }

  const headers = {};
  headersList.value.forEach(h => {
    if (h.key) headers[h.key] = h.value;
  });

  const values = {};
  valuesList.value.forEach(v => {
    if (v.key) values[v.key] = v.value;
  });

  const path_params = {};
  pathParamsList.value.forEach(p => {
    if (p.key) path_params[p.key] = p.value;
  });

  const query_params = {};
  queryParamsList.value.forEach(q => {
    if (q.key) query_params[q.key] = q.value;
  });

  const body_params = {};
  bodyParamsList.value.forEach(b => {
    if (b.key) {
      body_params[b.key] = {
        type: b.type || 'string',
        required: b.required || false,
        description: b.description || '',
      };
    }
  });

  const toolData = {
    name: httpToolForm.name,
    description: httpToolForm.description,
    method: httpToolForm.method,
    endpoint: httpToolForm.endpoint,
    headers,
    values,
    parameters: {
      path_params,
      query_params,
      body_params,
    },
    error_handling: {
      timeout: parseInt(httpToolForm.error_handling.timeout, 10) || 30,
      retry_count: parseInt(httpToolForm.error_handling.retry_count, 10) || 0,
      fallback_response: httpToolForm.error_handling.fallback_response || {},
    },
  };

  try {
    isSaving.value = true;
    let httpTools = [...(customTools.value.http_tools || [])];

    if (editingToolIndex.value !== null) {
      httpTools[editingToolIndex.value] = toolData;
    } else {
      httpTools.push(toolData);
    }

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      custom_tools: {
        ...customTools.value,
        http_tools: httpTools,
      },
    });

    customTools.value.http_tools = httpTools;
    httpToolDialogRef.value.close();
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('Ferramenta salva e sincronizada');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar ferramenta HTTP');
  } finally {
    isSaving.value = false;
  }
};

const deleteHttpTool = async index => {
  if (!props.agentId) {
    useAlert('Erro: ID do agente nao encontrado');
    return;
  }

  try {
    isSaving.value = true;
    const httpTools = customTools.value.http_tools.filter(
      (_, i) => i !== index
    );

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      custom_tools: {
        ...customTools.value,
        http_tools: httpTools,
      },
    });

    customTools.value.http_tools = httpTools;
    useAlert('Ferramenta excluida');
  } catch (error) {
    useAlert(error?.message || 'Erro ao excluir ferramenta');
  } finally {
    isSaving.value = false;
  }
};

// List management helpers
const addHeader = () => headersList.value.push({ key: '', value: '' });
const removeHeader = index => headersList.value.splice(index, 1);

const addValue = () => valuesList.value.push({ key: '', value: '' });
const removeValue = index => valuesList.value.splice(index, 1);

const addPathParam = () => pathParamsList.value.push({ key: '', value: '' });
const removePathParam = index => pathParamsList.value.splice(index, 1);

const addQueryParam = () => queryParamsList.value.push({ key: '', value: '' });
const removeQueryParam = index => queryParamsList.value.splice(index, 1);

const addBodyParam = () =>
  bodyParamsList.value.push({
    key: '',
    type: 'string',
    required: false,
    description: '',
  });
const removeBodyParam = index => bodyParamsList.value.splice(index, 1);

const getMethodColor = method => {
  const colors = {
    GET: 'bg-green-500/20 text-green-400',
    POST: 'bg-blue-500/20 text-blue-400',
    PUT: 'bg-yellow-500/20 text-yellow-400',
    DELETE: 'bg-red-500/20 text-red-400',
    PATCH: 'bg-purple-500/20 text-purple-400',
  };
  return colors[method] || 'bg-gray-500/20 text-gray-400';
};

const modalTabs = [
  { id: 'basic', label: 'Basico', icon: 'i-lucide-settings' },
  { id: 'headers', label: 'Headers', icon: 'i-lucide-file-code' },
  { id: 'params', label: 'Parametros', icon: 'i-lucide-list' },
  { id: 'values', label: 'Valores', icon: 'i-lucide-variable' },
  { id: 'errors', label: 'Erros', icon: 'i-lucide-alert-triangle' },
];
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <SettingsHeader
      heading="Ferramentas do Agente"
      description="Configure as ferramentas que o agente pode usar para executar acoes."
    />

    <!-- Action Button -->
    <div class="flex items-center justify-end mt-4 mb-6">
      <Button
        label="Adicionar HTTP Tool"
        icon="i-lucide-plus"
        @click="openHttpToolModal()"
      />
    </div>

    <!-- Agent Tools Summary -->
    <div
      v-if="agent"
      class="mb-6 p-4 bg-n-alpha-1 border border-n-weak rounded-xl"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-bot text-xl text-n-slate-11" />
        </div>
        <div>
          <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
          <p class="text-xs text-n-slate-10">
            {{ agentTools.length }} ferramentas ativas
          </p>
        </div>
      </div>
    </div>

    <!-- Tool Permissions Section -->
    <div class="mb-8">
      <h3
        class="text-md font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
      >
        <span class="i-lucide-shield-check text-n-slate-11" />
        Permissoes de Ferramentas Destrutivas
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        Por padrao, ferramentas que criam/alteram/removem dados estao
        <strong>bloqueadas</strong>. Habilite apenas o que voce quer que o
        agente possa fazer em nome do cliente. A nota opcional diz ao agente
        <em>quando</em> usar aquela acao.
      </p>
      <div
        v-for="(tools, groupName) in destructiveToolGroups"
        :key="groupName"
        class="mb-4"
      >
        <p class="text-xs font-semibold text-n-slate-10 uppercase mb-2">
          {{ groupName }}
        </p>
        <div class="grid gap-2">
          <div
            v-for="tool in tools"
            :key="tool.id"
            class="p-3 bg-n-solid-1 border border-n-weak rounded-xl"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="font-medium text-n-slate-12 text-sm">
                  {{ tool.label }}
                </p>
                <p class="text-xs text-n-slate-10 font-mono">{{ tool.id }}</p>
              </div>
              <Switch
                :model-value="getToolPerm(tool.id).enabled"
                @change="toggleToolPerm(tool.id)"
              />
            </div>
            <div v-if="getToolPerm(tool.id).enabled" class="mt-3">
              <Input
                :model-value="getToolPerm(tool.id).note"
                placeholder="Quando o agente deve usar esta acao? (opcional)"
                @update:model-value="val => updateToolNote(tool.id, val)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Built-in Tools Section -->
    <div class="mb-8">
      <h3
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-wrench text-n-slate-11" />
        Ferramentas Integradas
      </h3>
      <div class="grid gap-3">
        <div
          v-for="tool in builtinTools"
          :key="tool.id"
          class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="text-xl text-n-slate-11" :class="[tool.icon]" />
            </div>
            <div>
              <p class="font-medium text-n-slate-12">{{ tool.name }}</p>
              <p class="text-xs text-n-slate-10">{{ tool.description }}</p>
            </div>
          </div>
          <Switch
            :model-value="isToolEnabled(tool.id)"
            @change="toggleTool(tool.id)"
          />
        </div>
      </div>
    </div>

    <!-- Chatwoot Tools Section -->
    <div v-if="chatwootToolsTemplates.length > 0" class="mb-8">
      <h3
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-blocks text-n-slate-11" />
        Ferramentas Chatwoot
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        Habilite ferramentas para permitir que o agente interaja com recursos do
        Chatwoot como funis, produtos, agendamentos e mais.
      </p>

      <div v-if="isLoadingChatwootTools" class="flex justify-center py-8">
        <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="template in chatwootToolsTemplates"
          :key="template.id"
          class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center"
            >
              <span
                class="text-xl text-violet-400"
                :class="[getChatwootToolIcon(template.category)]"
              />
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <p class="font-medium text-n-slate-12">{{ template.name }}</p>
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded bg-violet-500/20 text-violet-400"
                >
                  {{
                    chatwootToolsCategories[template.category] ||
                    template.category
                  }}
                </span>
              </div>
              <p class="text-xs text-n-slate-10">{{ template.description }}</p>
              <div
                v-if="template.tools && template.tools.length > 0"
                class="flex flex-wrap gap-1 mt-1"
              >
                <span
                  v-for="tool in template.tools.slice(0, 4)"
                  :key="tool"
                  class="px-1.5 py-0.5 text-xs bg-n-alpha-2 text-n-slate-10 rounded"
                >
                  {{ tool }}
                </span>
                <span
                  v-if="template.tools.length > 4"
                  class="px-1.5 py-0.5 text-xs bg-n-alpha-2 text-n-slate-10 rounded"
                >
                  +{{ template.tools.length - 4 }}
                </span>
              </div>
            </div>
          </div>
          <Switch
            :model-value="isChatwootToolEnabled(template.id)"
            @change="toggleChatwootTool(template.id)"
          />
        </div>
      </div>
    </div>

    <!-- HTTP Tools Section -->
    <div class="mb-8">
      <h3
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-globe text-n-slate-11" />
        HTTP Tools (APIs Externas)
      </h3>

      <div
        v-if="!customTools.http_tools || customTools.http_tools.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
      >
        <div
          class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-globe text-2xl text-n-slate-10" />
        </div>
        <h4 class="text-md font-medium text-n-slate-12 mb-1">
          Nenhuma HTTP Tool configurada
        </h4>
        <p class="text-sm text-n-slate-11 mb-4 max-w-md">
          Adicione ferramentas HTTP para permitir que o agente chame APIs
          externas.
        </p>
        <Button
          label="Adicionar HTTP Tool"
          icon="i-lucide-plus"
          variant="secondary"
          @click="openHttpToolModal()"
        />
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="(tool, index) in customTools.http_tools"
          :key="index"
          class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-globe text-xl text-n-slate-11" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <p class="font-medium text-n-slate-12">{{ tool.name }}</p>
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded"
                  :class="getMethodColor(tool.method)"
                >
                  {{ tool.method }}
                </span>
              </div>
              <p class="text-xs text-n-slate-10 truncate max-w-md">
                {{ tool.endpoint }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-pencil"
              variant="ghost"
              size="sm"
              @click="openHttpToolModal(tool, index)"
            />
            <ConfirmButton
              icon="i-lucide-trash-2"
              variant="ghost"
              size="sm"
              color="ruby"
              label=""
              confirm-label=""
              confirm-hint="Confirmar?"
              @click="deleteHttpTool(index)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Info Card -->
    <div class="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
      <div class="flex gap-3">
        <span class="i-lucide-info text-blue-400 text-xl flex-shrink-0" />
        <div>
          <h4 class="font-medium text-n-slate-12 mb-1">Sobre as Ferramentas</h4>
          <p class="text-sm text-n-slate-11">
            Ferramentas permitem que o agente execute acoes como calculos,
            consultas a APIs, ou manipulacao de dados. O agente decide
            automaticamente quando usar cada ferramenta baseado no contexto da
            conversa.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- HTTP Tool Dialog -->
  <Dialog
    ref="httpToolDialogRef"
    :title="(editingToolIndex !== null ? 'Editar' : 'Adicionar') + ' HTTP Tool'"
    description="Configure uma ferramenta para chamar APIs externas."
    confirm-button-label="Salvar"
    :is-loading="isSaving"
    overflow-y-auto
    width="2xl"
    @confirm="saveHttpTool"
  >
    <!-- Internal Tabs -->
    <div
      class="flex gap-1 mb-4 border-b border-n-weak -mx-2 px-2 overflow-x-auto"
    >
      <button
        v-for="tab in modalTabs"
        :key="tab.id"
        class="px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap shrink-0"
        :class="
          activeTab === tab.id
            ? 'text-n-brand border-n-brand'
            : 'text-n-slate-11 border-transparent hover:text-n-slate-12'
        "
        @click="activeTab = tab.id"
      >
        <span :class="tab.icon" />
        {{ tab.label }}
      </button>
    </div>

    <!-- Basic Tab -->
    <div v-show="activeTab === 'basic'" class="space-y-4">
      <Input
        v-model="httpToolForm.name"
        label="Nome da Ferramenta"
        placeholder="get_weather"
        message="Use snake_case sem espacos"
      />

      <Input
        v-model="httpToolForm.description"
        label="Descricao"
        placeholder="Obtem a previsao do tempo para uma cidade"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12"> Metodo HTTP </label>
        <ComboBox
          v-model="httpToolForm.method"
          :options="httpMethodOptions"
          placeholder="Selecione..."
        />
      </div>

      <Input
        v-model="httpToolForm.endpoint"
        label="Endpoint URL"
        placeholder="https://api.example.com/v1/weather/{city}"
        message="Use {param} para parametros de path"
      />
    </div>

    <!-- Headers Tab -->
    <div v-show="activeTab === 'headers'" class="space-y-4">
      <div class="flex items-center justify-between mb-2">
        <p class="text-sm text-n-slate-11">
          Headers HTTP enviados com a requisicao
        </p>
        <Button
          label="Adicionar Header"
          icon="i-lucide-plus"
          variant="ghost"
          size="sm"
          @click="addHeader"
        />
      </div>

      <div v-if="headersList.length === 0" class="text-center py-8">
        <p class="text-sm text-n-slate-10">Nenhum header configurado</p>
      </div>

      <div v-else class="space-y-2">
        <div
          v-for="(header, index) in headersList"
          :key="index"
          class="flex items-center gap-2"
        >
          <Input
            v-model="header.key"
            placeholder="Header Name"
            size="sm"
            custom-input-class="flex-1"
          />
          <Input
            v-model="header.value"
            placeholder="Value (use {var} for placeholders)"
            size="sm"
            custom-input-class="flex-1"
          />
          <Button
            icon="i-lucide-x"
            variant="ghost"
            size="sm"
            @click="removeHeader(index)"
          />
        </div>
      </div>

      <div class="p-3 bg-n-alpha-1 rounded-lg text-sm text-n-slate-11">
        <p class="font-medium mb-1">Dica:</p>
        <p>
          Use <code class="bg-n-alpha-2 px-1 rounded">{'{token}'}</code>
          para criar placeholders que serao substituidos pelos valores
          configurados.
        </p>
      </div>
    </div>

    <!-- Parameters Tab -->
    <div v-show="activeTab === 'params'" class="space-y-6">
      <!-- Path Parameters -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <div>
            <p class="text-sm font-medium text-n-slate-12">Path Parameters</p>
            <p class="text-xs text-n-slate-10">
              Parametros na URL (ex: /users/{'{id}'}/)
            </p>
          </div>
          <Button
            icon="i-lucide-plus"
            variant="ghost"
            size="sm"
            @click="addPathParam"
          />
        </div>
        <div v-if="pathParamsList.length === 0" class="text-center py-4">
          <p class="text-xs text-n-slate-10">Nenhum path param</p>
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="(param, index) in pathParamsList"
            :key="index"
            class="flex items-center gap-2"
          >
            <Input
              v-model="param.key"
              placeholder="Nome"
              size="sm"
              custom-input-class="w-1/3"
            />
            <Input
              v-model="param.value"
              placeholder="Descricao"
              size="sm"
              custom-input-class="flex-1"
            />
            <Button
              icon="i-lucide-x"
              variant="ghost"
              size="sm"
              @click="removePathParam(index)"
            />
          </div>
        </div>
      </div>

      <!-- Query Parameters -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <div>
            <p class="text-sm font-medium text-n-slate-12">Query Parameters</p>
            <p class="text-xs text-n-slate-10">
              Parametros de query string (ex: ?filter=active)
            </p>
          </div>
          <Button
            icon="i-lucide-plus"
            variant="ghost"
            size="sm"
            @click="addQueryParam"
          />
        </div>
        <div v-if="queryParamsList.length === 0" class="text-center py-4">
          <p class="text-xs text-n-slate-10">Nenhum query param</p>
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="(param, index) in queryParamsList"
            :key="index"
            class="flex items-center gap-2"
          >
            <Input
              v-model="param.key"
              placeholder="Nome"
              size="sm"
              custom-input-class="w-1/3"
            />
            <Input
              v-model="param.value"
              placeholder="Descricao"
              size="sm"
              custom-input-class="flex-1"
            />
            <Button
              icon="i-lucide-x"
              variant="ghost"
              size="sm"
              @click="removeQueryParam(index)"
            />
          </div>
        </div>
      </div>

      <!-- Body Parameters -->
      <div v-if="httpToolForm.method !== 'GET'">
        <div class="flex items-center justify-between mb-2">
          <div>
            <p class="text-sm font-medium text-n-slate-12">Body Parameters</p>
            <p class="text-xs text-n-slate-10">
              Campos do corpo da requisicao (JSON)
            </p>
          </div>
          <Button
            icon="i-lucide-plus"
            variant="ghost"
            size="sm"
            @click="addBodyParam"
          />
        </div>
        <div v-if="bodyParamsList.length === 0" class="text-center py-4">
          <p class="text-xs text-n-slate-10">Nenhum body param</p>
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="(param, index) in bodyParamsList"
            :key="index"
            class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg space-y-2"
          >
            <div class="flex items-center gap-2">
              <Input
                v-model="param.key"
                placeholder="Nome do campo"
                size="sm"
                custom-input-class="flex-1"
              />
              <ComboBox
                v-model="param.type"
                :options="paramTypeOptions"
                placeholder="Tipo"
              />
              <div class="flex items-center gap-1">
                <Switch v-model="param.required" />
                <span class="text-xs text-n-slate-11">Obrig.</span>
              </div>
              <Button
                icon="i-lucide-x"
                variant="ghost"
                size="sm"
                @click="removeBodyParam(index)"
              />
            </div>
            <Input
              v-model="param.description"
              placeholder="Descricao do campo"
              size="sm"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Values Tab -->
    <div v-show="activeTab === 'values'" class="space-y-4">
      <div class="flex items-center justify-between mb-2">
        <div>
          <p class="text-sm font-medium text-n-slate-12">Valores Padrao</p>
          <p class="text-xs text-n-slate-10">
            Valores que serao usados nos placeholders
          </p>
        </div>
        <Button
          label="Adicionar Valor"
          icon="i-lucide-plus"
          variant="ghost"
          size="sm"
          @click="addValue"
        />
      </div>

      <div v-if="valuesList.length === 0" class="text-center py-8">
        <p class="text-sm text-n-slate-10">Nenhum valor configurado</p>
      </div>

      <div v-else class="space-y-2">
        <div
          v-for="(value, index) in valuesList"
          :key="index"
          class="flex items-center gap-2"
        >
          <Input
            v-model="value.key"
            placeholder="Nome da variavel"
            size="sm"
            custom-input-class="w-1/3"
          />
          <Input
            v-model="value.value"
            placeholder="Valor padrao"
            size="sm"
            custom-input-class="flex-1"
          />
          <Button
            icon="i-lucide-x"
            variant="ghost"
            size="sm"
            @click="removeValue(index)"
          />
        </div>
      </div>

      <div class="p-3 bg-n-alpha-1 rounded-lg text-sm text-n-slate-11">
        <p class="font-medium mb-1">Exemplo:</p>
        <p>
          Se voce tem um header
          <code class="bg-n-alpha-2 px-1 rounded"
            >Authorization: Bearer {{ '{token}' }}</code
          >, adicione um valor com nome <strong>token</strong> e o valor da sua
          API key.
        </p>
      </div>
    </div>

    <!-- Error Handling Tab -->
    <div v-show="activeTab === 'errors'" class="space-y-4">
      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model="httpToolForm.error_handling.timeout"
          label="Timeout (segundos)"
          type="number"
          min="1"
          max="120"
        />
        <Input
          v-model="httpToolForm.error_handling.retry_count"
          label="Tentativas de Retry"
          type="number"
          min="0"
          max="5"
        />
      </div>

      <div class="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
        <div class="flex gap-2">
          <span class="i-lucide-alert-triangle text-yellow-500 flex-shrink-0" />
          <p class="text-sm text-n-slate-11">
            Se a API falhar apos as tentativas, o agente informara ao usuario
            que nao conseguiu obter os dados.
          </p>
        </div>
      </div>
    </div>
  </Dialog>
</template>
